import { useEffect, useState } from 'react'
import { AlertTriangle, CheckCircle2, Search } from 'lucide-react'
import api from '@/lib/api.ts'
import {
  Badge,
  Button,
  Checkbox,
  Input,
  Modal,
} from '@/components/ui'

type Portal = 'also' | 'eset'

interface SapPartner {
  CardCode: string
  CardName: string
  EmailAddress?: string
  Phone1?: string
  City?: string
}

interface MappedFields {
  cardCode: string
  name: string
  street: string
  zip: string
  city: string
  country: string
  phone: string
  email: string
  domain: string
  technicalEmail: string
}

interface TestCreateDiag {
  parentAccountId?: number
  parentSource?: 'config' | 'getReseller'
  cfgResellerAccountId?: string | number
  resellerLookupError?: string
}

function useSapSearch(open: boolean) {
  const [term, setTerm] = useState('')
  const [results, setResults] = useState<SapPartner[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open) {
      setTerm('')
      setResults([])
      return
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const q = term.trim()
    if (q.length < 2) {
      setResults([])
      return
    }
    let cancelled = false
    setLoading(true)
    const handle = setTimeout(() => {
      api
        .get<SapPartner[]>(`/sap/partners`, { params: { search: q } })
        .then((r) => {
          if (!cancelled) setResults(r.data ?? [])
        })
        .catch(() => {
          if (!cancelled) setResults([])
        })
        .finally(() => {
          if (!cancelled) setLoading(false)
        })
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(handle)
    }
  }, [term, open])

  return { term, setTerm, results, loading }
}

function PartnerSearch({
  selected,
  onSelect,
  open,
}: {
  selected: SapPartner | null
  onSelect: (p: SapPartner | null) => void
  open: boolean
}) {
  const { term, setTerm, results, loading } = useSapSearch(open)
  if (selected) {
    return (
      <div className="flex items-center justify-between gap-3 rounded border border-gray-200 bg-gray-50 px-3 py-2">
        <div className="min-w-0">
          <div className="font-medium text-gray-900 truncate">{selected.CardName}</div>
          <div className="text-caption text-gray-500 tabular-nums">{selected.CardCode}</div>
        </div>
        <Button variant="ghost" size="xs" onClick={() => onSelect(null)}>
          Ändern
        </Button>
      </div>
    )
  }
  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          autoFocus
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="SAP-Kunde suchen (Name oder CardCode)…"
          className="pl-8"
        />
      </div>
      {term.trim().length >= 2 ? (
        <div className="max-h-56 overflow-y-auto rounded border border-gray-200 divide-y divide-gray-100">
          {loading ? (
            <div className="px-3 py-2 text-caption text-gray-500">Suche läuft…</div>
          ) : results.length === 0 ? (
            <div className="px-3 py-2 text-caption text-gray-500">Keine Treffer</div>
          ) : (
            results.slice(0, 30).map((p) => (
              <button
                key={p.CardCode}
                type="button"
                onClick={() => onSelect(p)}
                className="w-full text-left px-3 py-2 hover:bg-gray-50 focus:outline-none focus:bg-gray-50"
              >
                <div className="font-medium text-gray-900 truncate">{p.CardName}</div>
                <div className="text-caption text-gray-500 tabular-nums">
                  {p.CardCode}
                  {p.City ? ` · ${p.City}` : ''}
                  {p.EmailAddress ? ` · ${p.EmailAddress}` : ''}
                </div>
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  )
}

// ─── Test-Create-Modal (Feature A) ──────────────────────────────────────

export function TestCreateCustomerModal({
  open,
  onOpenChange,
  portal,
  onSuccess,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  portal: Portal
  onSuccess?: () => void
}) {
  const [selected, setSelected] = useState<SapPartner | null>(null)
  const [fields, setFields] = useState<MappedFields | null>(null)
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [diag, setDiag] = useState<TestCreateDiag | null>(null)
  const [result, setResult] = useState<{ portalId: string; name: string } | null>(null)

  useEffect(() => {
    if (!open) {
      setSelected(null)
      setFields(null)
      setError(null)
      setDiag(null)
      setResult(null)
    }
  }, [open])

  useEffect(() => {
    if (!selected) {
      setFields(null)
      return
    }
    setLoading(true)
    setError(null)
    api
      .post('/einkauf/supplier-overview/sap-preview', { sapCardCode: selected.CardCode })
      .then((r) => setFields(r.data.fields))
      .catch((err) => setError(extractErr(err)))
      .finally(() => setLoading(false))
  }, [selected])

  const portalLabel = portal === 'also' ? 'ALSO' : 'ESET'

  async function submit() {
    if (!selected || !fields) return
    setSubmitting(true)
    setError(null)
    setDiag(null)
    try {
      const r = await api.post('/einkauf/supplier-overview/test-create-customer', {
        portal,
        sapCardCode: selected.CardCode,
        overrides: fields,
      })
      setResult({ portalId: r.data.portalId, name: r.data.name })
      if (r.data.diag) setDiag(r.data.diag as TestCreateDiag)
      onSuccess?.()
    } catch (err) {
      setError(extractErr(err))
      const e = err as { response?: { data?: { diag?: TestCreateDiag } } }
      if (e.response?.data?.diag) setDiag(e.response.data.diag)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={`Testkunde in ${portalLabel} anlegen`}
      description="SAP-Kunde wählen, Felder prüfen, anlegen. CardCode wird als externe Referenz mitgespeichert."
      footer={
        result ? (
          <div className="flex justify-end">
            <Button variant="primary" onClick={() => onOpenChange(false)}>
              Schließen
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
              Abbrechen
            </Button>
            <Button
              variant="primary"
              onClick={submit}
              disabled={!selected || !fields || submitting || loading}
            >
              {submitting ? 'Lege an…' : `In ${portalLabel} anlegen`}
            </Button>
          </div>
        )
      }
    >
      {result ? (
        <div className="space-y-2">
          <div className="flex items-start gap-3 rounded border border-green-200 bg-green-50 p-3">
            <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
            <div className="text-small">
              <div className="font-medium text-green-900">
                {portalLabel}-Kunde angelegt: {result.name}
              </div>
              <div className="text-green-800 mt-0.5">
                Portal-ID: <span className="font-mono tabular-nums">{result.portalId}</span>
              </div>
            </div>
          </div>
          {diag ? <DiagBox diag={diag} /> : null}
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <div className="text-caption text-gray-500 mb-1">SAP-Kunde</div>
            <PartnerSearch selected={selected} onSelect={setSelected} open={open} />
          </div>

          {loading ? (
            <div className="text-small text-gray-500">SAP-Daten werden geladen…</div>
          ) : null}

          {fields ? (
            <div className="grid grid-cols-2 gap-3">
              <FieldInput
                label="Name"
                value={fields.name}
                onChange={(v) => setFields({ ...fields, name: v })}
              />
              <FieldInput
                label="KdNr (CardCode)"
                value={fields.cardCode}
                disabled
              />
              <FieldInput
                label="Straße"
                value={fields.street}
                onChange={(v) => setFields({ ...fields, street: v })}
              />
              <div className="grid grid-cols-[100px_1fr] gap-2">
                <FieldInput
                  label="PLZ"
                  value={fields.zip}
                  onChange={(v) => setFields({ ...fields, zip: v })}
                />
                <FieldInput
                  label="Ort"
                  value={fields.city}
                  onChange={(v) => setFields({ ...fields, city: v })}
                />
              </div>
              <FieldInput
                label="Telefon"
                value={fields.phone}
                onChange={(v) => setFields({ ...fields, phone: v })}
              />
              <FieldInput
                label="E-Mail"
                value={fields.email}
                onChange={(v) => {
                  const domain = v.includes('@') ? v.split('@')[1]!.trim().toLowerCase() : ''
                  const technicalEmail = domain ? `admin@${domain}` : ''
                  setFields({ ...fields, email: v, domain, technicalEmail })
                }}
              />
              <FieldInput
                label="Domain"
                value={fields.domain}
                onChange={(v) => {
                  const technicalEmail = v ? `admin@${v}` : ''
                  setFields({ ...fields, domain: v, technicalEmail })
                }}
              />
              <FieldInput
                label="Technical Contact E-Mail"
                value={fields.technicalEmail}
                onChange={(v) => setFields({ ...fields, technicalEmail: v })}
              />
            </div>
          ) : null}

          {error ? <ErrorBox message={error} /> : null}
          {diag && portal === 'also' ? <DiagBox diag={diag} /> : null}
        </div>
      )}
    </Modal>
  )
}

// ─── Map-Customer-Modal (Feature B) ─────────────────────────────────────

export function MapCustomerModal({
  open,
  onOpenChange,
  portal,
  portalId,
  portalName,
  onSuccess,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  portal: Portal
  portalId: string
  portalName?: string
  onSuccess?: () => void
}) {
  const [selected, setSelected] = useState<SapPartner | null>(null)
  const [writeback, setWriteback] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<{ writebackWarning?: string } | null>(null)

  useEffect(() => {
    if (!open) {
      setSelected(null)
      setWriteback(true)
      setError(null)
      setResult(null)
    }
  }, [open])

  const portalLabel = portal === 'also' ? 'ALSO' : 'ESET'

  async function submit() {
    if (!selected) return
    setSubmitting(true)
    setError(null)
    try {
      const r = await api.post('/einkauf/supplier-overview/map-customer', {
        portal,
        portalId,
        sapCardCode: selected.CardCode,
        writeback,
      })
      setResult({ writebackWarning: r.data.writebackWarning })
      onSuccess?.()
    } catch (err) {
      setError(extractErr(err))
    } finally {
      setSubmitting(false)
    }
  }

  const writebackTargetLabel =
    portal === 'also' ? 'CustomerId in ALSO' : 'customIdentifier in ESET'

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      title={`${portalLabel}-Eintrag SAP zuordnen`}
      description={
        portalName ? (
          <span>
            <Badge variant="neutral" size="sm">
              {portalLabel}
            </Badge>{' '}
            {portalName}{' '}
            <span className="font-mono text-caption">({portalId})</span>
          </span>
        ) : (
          <span>Portal-ID: <span className="font-mono">{portalId}</span></span>
        )
      }
      footer={
        result ? (
          <div className="flex justify-end">
            <Button variant="primary" onClick={() => onOpenChange(false)}>
              Schließen
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
              Abbrechen
            </Button>
            <Button
              variant="primary"
              onClick={submit}
              disabled={!selected || submitting}
            >
              {submitting ? 'Speichere…' : 'Zuordnen'}
            </Button>
          </div>
        )
      }
    >
      {result ? (
        <div className="space-y-3">
          <div className="flex items-start gap-3 rounded border border-green-200 bg-green-50 p-3">
            <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
            <div className="text-small">
              <div className="font-medium text-green-900">Zuordnung gespeichert</div>
              <div className="text-green-800 mt-0.5">
                {portalLabel}-ID {portalId} ist jetzt mit dem SAP-Kunden verknüpft.
              </div>
            </div>
          </div>
          {result.writebackWarning ? (
            <div className="flex items-start gap-3 rounded border border-amber-200 bg-amber-50 p-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
              <div className="text-small">
                <div className="font-medium text-amber-900">
                  Writeback ins Portal fehlgeschlagen
                </div>
                <div className="text-amber-800 mt-0.5">
                  Der CardCode konnte nicht in {writebackTargetLabel} zurückgeschrieben werden.
                  Lokales Mapping ist trotzdem aktiv.
                </div>
                <details className="mt-2">
                  <summary className="cursor-pointer text-caption text-amber-900">
                    Details
                  </summary>
                  <pre className="mt-1 whitespace-pre-wrap break-all text-caption text-amber-900">
                    {result.writebackWarning}
                  </pre>
                </details>
              </div>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <div className="text-caption text-gray-500 mb-1">SAP-Kunde</div>
            <PartnerSearch selected={selected} onSelect={setSelected} open={open} />
          </div>
          <Checkbox
            label={`CardCode in ${writebackTargetLabel} zurückschreiben`}
            hint="Bei Fehler bleibt das lokale Mapping bestehen, eine Warnung wird angezeigt."
            checked={writeback}
            onChange={(e) => setWriteback(e.target.checked)}
          />
          {error ? <ErrorBox message={error} /> : null}
        </div>
      )}
    </Modal>
  )
}

// ─── Helpers ─────────────────────────────────────────────────────────────

function FieldInput({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string
  value: string
  onChange?: (v: string) => void
  disabled?: boolean
}) {
  return (
    <div>
      <div className="text-caption text-gray-500 mb-0.5">{label}</div>
      <Input
        value={value ?? ''}
        onChange={(e) => onChange?.(e.target.value)}
        disabled={disabled}
      />
    </div>
  )
}

function ErrorBox({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-3 rounded border border-red-200 bg-red-50 p-3">
      <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
      <div className="text-small text-red-900 whitespace-pre-wrap break-all">{message}</div>
    </div>
  )
}

function extractErr(err: unknown): string {
  const e = err as { response?: { data?: { error?: string; message?: string } }; message?: string }
  return e.response?.data?.error ?? e.response?.data?.message ?? e.message ?? 'Unbekannter Fehler'
}

function DiagBox({ diag }: { diag: TestCreateDiag }) {
  return (
    <div className="rounded border border-gray-200 bg-gray-50 p-3 text-caption space-y-1">
      <div className="font-medium text-gray-700">ALSO-Diagnose</div>
      <div className="grid grid-cols-[180px_1fr] gap-x-3 gap-y-0.5">
        <span className="text-gray-500">ParentAccountId benutzt:</span>
        <span className="font-mono tabular-nums text-gray-900">
          {diag.parentAccountId ?? '—'}
        </span>
        <span className="text-gray-500">Quelle:</span>
        <span className="text-gray-900">
          {diag.parentSource === 'getReseller'
            ? '/GetReseller (auto-detect)'
            : diag.parentSource === 'config'
              ? 'Adapter-Config (manuell)'
              : '—'}
        </span>
        <span className="text-gray-500">Config-Wert:</span>
        <span className="font-mono tabular-nums text-gray-900">
          {diag.cfgResellerAccountId ?? '—'}
        </span>
        {diag.resellerLookupError ? (
          <>
            <span className="text-gray-500">/GetReseller-Fehler:</span>
            <span className="text-red-700 break-all">{diag.resellerLookupError}</span>
          </>
        ) : null}
      </div>
    </div>
  )
}
